import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { FolderKanban, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

export const AssignHubModal = ({ 
  isOpen, 
  onClose, 
  hub,
  workspaces,
  currentAssignment,
  onAssign,
  onRemoveAssignment
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState(currentAssignment || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleAssign = async () => {
    if (!selectedWorkspace) {
      // Remove assignment
      setIsLoading(true);
      const success = await onRemoveAssignment?.(hub.id);
      setIsLoading(false);
      
      if (success) {
        toast.success(`"${hub.name}" moved to Unorganized`);
        onClose();
      }
      return;
    }

    setIsLoading(true);
    const success = await onAssign?.(hub.id, selectedWorkspace);
    setIsLoading(false);
    
    if (success) {
      const ws = workspaces.find(w => w.id === selectedWorkspace);
      toast.success(`"${hub.name}" assigned to "${ws?.name}"`);
      onClose();
    } else {
      toast.error('Failed to assign hub');
    }
  };

  const handleClose = () => {
    setSelectedWorkspace(currentAssignment || '');
    onClose();
  };

  if (!isOpen || !hub) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white dark:bg-gray-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderKanban className="text-purple-500" size={20} />
            Organize Hub
          </DialogTitle>
          <DialogDescription>
            Choose which workspace to display "{hub.name}" under for your personal view.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-3">
          {/* Hub info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: hub.color || '#6366F1' }}
            >
              {hub.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{hub.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Invited hub • {hub.members?.length || 0} members
              </p>
            </div>
          </div>

          {/* Workspace selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Assign to workspace:
            </label>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {/* Unorganized option */}
              <button
                onClick={() => setSelectedWorkspace('')}
                className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left",
                  !selectedWorkspace
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-200 dark:border-gray-600 hover:border-gray-300"
                )}
              >
                <div className="w-8 h-8 rounded bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                  <FolderKanban size={16} className="text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Unorganized</p>
                  <p className="text-xs text-gray-500">Show separately from workspaces</p>
                </div>
                {!selectedWorkspace && <Check size={18} className="text-blue-500" />}
              </button>

              {/* Workspace options */}
              {workspaces.map(ws => (
                <button
                  key={ws.id}
                  onClick={() => setSelectedWorkspace(ws.id)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left",
                    selectedWorkspace === ws.id
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300"
                  )}
                >
                  <div 
                    className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: ws.brand_color || '#3B82F6' }}
                  >
                    {ws.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{ws.name}</p>
                    <p className="text-xs text-gray-500">Your workspace</p>
                  </div>
                  {selectedWorkspace === ws.id && <Check size={18} className="text-blue-500" />}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            This only changes how you see this hub. Other members won't be affected.
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign} 
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
